import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { AlertCircle, Fingerprint, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { EqmsAction, EqmsSignatureMeaning, EqmsTransitionInput } from '../../types/eqms.types';
import { getSignatureErrorMessage } from '../../utils/signatureError';

const ACTION_LABELS: Record<EqmsAction, string> = {
  register: 'Place under document control',
  submit_review: 'Submit for review',
  reject: 'Reject to draft',
  approve: 'Approve document',
  make_effective: 'Release as effective',
  obsolete: 'Make document obsolete',
  discard: 'Discard draft',
};

const ACTION_MEANINGS: Record<EqmsAction, EqmsSignatureMeaning> = {
  register: 'authorship',
  submit_review: 'responsibility',
  reject: 'review',
  approve: 'approval',
  make_effective: 'release',
  obsolete: 'obsolescence',
  discard: 'withdrawal',
};

// Matches the controlled-document API's identifier rule, not the document title.
const CONTROL_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]*$/;

interface ElectronicSignatureDialogProps {
  open: boolean;
  action: EqmsAction;
  documentName: string;
  defaultUsername: string;
  canExcludeFromTraining?: boolean;
  /** Server-owned capability to register an upload directly as Effective. */
  canImportEffective?: boolean;
  registrationMetadata?: { controlCode: string; department: string };
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: EqmsTransitionInput) => Promise<void>;
}

export function ElectronicSignatureDialog({
  open,
  action,
  documentName,
  defaultUsername,
  canExcludeFromTraining = false,
  canImportEffective = false,
  registrationMetadata,
  isPending,
  onOpenChange,
  onConfirm,
}: ElectronicSignatureDialogProps) {
  const { t } = useTranslation('qms');
  const registrationControlCode = registrationMetadata?.controlCode;
  const registrationDepartment = registrationMetadata?.department;
  const [reason, setReason] = useState('');
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');
  const [controlCode, setControlCode] = useState('');
  const [department, setDepartment] = useState('');
  const [excludeFromTraining, setExcludeFromTraining] = useState(false);
  const [importEffective, setImportEffective] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    setSubmissionError(null);
    setPassword('');
    setExcludeFromTraining(false);
    setImportEffective(false);
    if (!open) return;
    setReason('');
    setUsername(defaultUsername);
    if (registrationControlCode !== undefined && registrationDepartment !== undefined) {
      setControlCode(registrationControlCode);
      setDepartment(registrationDepartment);
    } else if (action !== 'register') {
      setControlCode('');
      setDepartment('');
    }
  }, [action, defaultUsername, open, registrationControlCode, registrationDepartment]);

  const importing = action === 'register' && canImportEffective && importEffective;
  const releasing = action === 'make_effective' || importing;
  const discarding = action === 'discard';
  const meaning = importing ? 'release' : ACTION_MEANINGS[action];
  const trimmedControlCode = controlCode.trim();
  const controlCodeInvalid = action === 'register' && trimmedControlCode.length > 0
    && (trimmedControlCode.length < 2 || trimmedControlCode.length > 64 || !CONTROL_CODE_PATTERN.test(trimmedControlCode));
  const canSubmit = reason.trim().length >= 3
    && username.trim().length > 0
    && password.length > 0
    && (action !== 'register' || (trimmedControlCode.length >= 2 && !controlCodeInvalid && department.trim().length >= 2));

  const handleConfirm = async () => {
    if (isPending || !canSubmit) return;
    setSubmissionError(null);
    try {
      await onConfirm({
        action,
        reason: reason.trim(),
        meaning,
        username: username.trim(),
        password,
        ...(releasing && canExcludeFromTraining
          ? { trainingRequired: !excludeFromTraining }
          : {}),
        ...(importing ? { importEffective: true } : {}),
        ...(action === 'register'
          ? { controlCode: controlCode.trim(), department: department.trim() }
          : {}),
      });
    } catch (error) {
      setSubmissionError(getSignatureErrorMessage(error, (key, fallback) => t(`signature.errors.${key}`, { defaultValue: fallback })));
      setPassword('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!isPending) onOpenChange(next); }}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <Fingerprint className="h-5 w-5" />
          </div>
          <DialogTitle>{t(`signature.actions.${action}`, { defaultValue: ACTION_LABELS[action] })}</DialogTitle>
          <DialogDescription>
            {t('signature.descriptionBefore', { defaultValue: 'Your credentials bind this action to' })}{' '}
            <span className="font-medium text-slate-800">{documentName}</span>.{' '}
            {t('signature.descriptionAfter', { defaultValue: 'The manifestation and reason become permanent audit evidence.' })}
          </DialogDescription>
        </DialogHeader>

        {submissionError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Signature not confirmed</AlertTitle>
            <AlertDescription>{submissionError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-2">
          {action === 'register' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="eqms-control-code">{t('signature.controlCode', { defaultValue: "Control code" })}</Label>
                <Input
                  id="eqms-control-code"
                  readOnly={Boolean(registrationMetadata)}
                  value={controlCode}
                  onChange={(event) => setControlCode(event.target.value.toUpperCase())}
                  placeholder="SOP-QA-001"
                  autoComplete="off"
                  aria-invalid={controlCodeInvalid}
                  aria-describedby="eqms-control-code-help"
                />
                <p id="eqms-control-code-help" role={controlCodeInvalid ? 'alert' : undefined}
                  className={`text-xs ${controlCodeInvalid ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {controlCodeInvalid
                    ? t('signature.controlInvalid', { defaultValue: "Use 2–64 characters: A–Z, 0–9, - _ . /; start with a letter or number. No spaces, accents or apostrophes." })
                    : t('signature.controlHelp', { defaultValue: "Use an identifier such as MAN-EMP-001 (2–64 characters), not the document title." })}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="eqms-department">{t('signature.department', { defaultValue: "Department" })}</Label>
                <Input
                  id="eqms-department"
                  readOnly={Boolean(registrationMetadata)}
                  value={department}
                  onChange={(event) => setDepartment(event.target.value)}
                  placeholder="Quality"
                />
              </div>
            </div>
          )}

          {registrationMetadata && action === 'register' && <p className="text-xs text-slate-500">{t('authoring.registrationMetadata')}</p>}

          {action === 'register' && canImportEffective && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
              <Checkbox
                id="eqms-import-effective"
                checked={importEffective}
                onCheckedChange={(checked) => {
                  setImportEffective(checked === true);
                  if (checked !== true) setExcludeFromTraining(false);
                }}
                disabled={isPending}
                aria-describedby="eqms-import-effective-help"
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="eqms-import-effective">{t('signature.importEffective', { defaultValue: "Import as already effective" })}</Label>
                <p id="eqms-import-effective-help" className="text-xs text-amber-900">
                  {t('signature.importEffectiveHint', { defaultValue: "Only for a document already effective in a previous system. Review and approval are skipped: the document becomes Effective (v1.0) as soon as you sign, and the bypass is recorded with your signature." })}
                </p>
              </div>
            </div>
          )}

          {discarding && (
            <Alert variant="destructive" className="border-red-200 bg-red-50/70">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('signature.discardTitle', { defaultValue: "This draft will leave the register" })}</AlertTitle>
              <AlertDescription>
                {t('signature.discardWarning', { defaultValue: "It never takes effect and cannot be restored. Its file, signatures and audit trail are kept as evidence, and its control code becomes free to reuse." })}
              </AlertDescription>
            </Alert>
          )}

          <div className="rounded-lg border border-teal-100 bg-teal-50/70 px-3 py-2 text-sm text-teal-900">
            <div className="flex items-center gap-2 font-medium">
              <ShieldCheck className="h-4 w-4" />
              {t('signature.meaning', { defaultValue: "Signature meaning" })}: {t(`signature.meanings.${meaning}`, { defaultValue: meaning })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="eqms-reason">
              {importing
                ? t('signature.bypassReason', { defaultValue: "Reason for bypassing review and approval" })
                : discarding
                  ? t('signature.discardReason', { defaultValue: "Reason for discarding" })
                  : t('signature.reason', { defaultValue: "Reason" })}
            </Label>
            <Textarea
              id="eqms-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={importing
                ? t('signature.bypassReasonHint', { defaultValue: "Explain why this document can skip review and approval, e.g. effective since 2019 in the paper QMS…" })
                : discarding
                  ? t('signature.discardReasonHint', { defaultValue: "e.g. uploaded by mistake, or no longer pursued…" })
                  : t('signature.reasonHint', { defaultValue: "State the purpose and evidence for this action…" })}
              rows={3}
            />
          </div>

          {releasing && canExcludeFromTraining && (
            <div className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
              <Checkbox
                id="eqms-no-training"
                checked={excludeFromTraining}
                onCheckedChange={(checked) => setExcludeFromTraining(checked === true)}
                disabled={isPending}
                aria-describedby="eqms-no-training-help"
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="eqms-no-training">{t('signature.noTraining', { defaultValue: "No training required" })}</Label>
                <p id="eqms-no-training-help" className="text-xs text-muted-foreground">
                  {t('signature.noTrainingHint', { defaultValue: "Make this document effective without assigning training. This choice is recorded with your signed release." })}
                </p>
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="eqms-username">{t('signature.username', { defaultValue: "Username" })}</Label>
              <Input
                id="eqms-username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eqms-password">{t('signature.password', { defaultValue: "Password" })}</Label>
              <Input
                id="eqms-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('authoring.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit || isPending}
            className={discarding ? 'bg-red-700 hover:bg-red-800' : 'bg-teal-700 hover:bg-teal-800'}>
            {isPending ? t('signature.signing', { defaultValue: "Signing…" }) : t('signature.confirm', { defaultValue: "Sign and confirm" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
